import React from 'react';
import useAppForm from '@app/site/_form';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import * as z from 'zod/v4';
import OperationType from '@app/model/company_hotel/settings/OperationType';
import Month from '@app/model/market_state/Month';
import { RadioField } from '@app/site/_form/field/RadioField';
import _capitalize from 'lodash/capitalize';
import { SubmitButton } from '@app/site/_form/button/SubmitButton';
import { ResetButton } from '@app/site/_form/button/ResetButton';
import Utilities from '@util/Utilities';
import { Field, Label } from '@headlessui/react';
import {
	useSaveCompanyHotelGeneralSettingsMutation,
} from '@app/model/company_hotel/service/CompanyHotelSettingsServiceHooks';
import useToaster from '@app/component/overlays/toaster/ToasterHook';


const RateshopperGeneralSettingsPage = React.memo(() => {

	const { companyHotel } = useSelectedCompanyHotel();
	const toaster = useToaster();

	const FormValidation = z.object({
		currency: z.enum(['EUR', 'USD', 'GBP']),
		ignorePartnerOffers: z.boolean(),
		nights: z.array(
			z.number({ message: 'Nights must be a number' })
				.int('Nights must be a whole number')
				.min(1, 'Nights must be at least 1'),
		)
			.min(1, 'Please select at least one nights value')
			.max(2, 'Please select at most two nights value'),
		operation: z.object({
			type: z.enum(Object.values(OperationType)),
			seasonFrom: z.preprocess(
				(v) => (v === '' || v === null ? undefined : v),
				z.enum(Object.values(Month)).optional(),
			),
			seasonTo: z.preprocess(
				(v) => (v === '' || v === null ? undefined : v),
				z.enum(Object.values(Month)).optional(),
			),
		}).superRefine((operation, ctx) => {
			if (operation.type === OperationType.SEASONAL) {
				if (!operation.seasonFrom) {
					ctx.addIssue({
						code: 'custom',
						path: ['seasonFrom'],
						message: 'Season start month is required',
					});
				}
				if (!operation.seasonTo) {
					ctx.addIssue({
						code: 'custom',
						path: ['seasonTo'],
						message: 'Season end month is required',
					});
				}
				if (!!operation.seasonFrom && !!operation.seasonTo) {
					const fromOrdinal = Utilities.getMonthOrdinal(operation.seasonFrom);
					const toOrdinal = Utilities.getMonthOrdinal(operation.seasonTo);
					if (toOrdinal <= fromOrdinal) {
						ctx.addIssue({
							code: 'custom',
							path: ['seasonTo'],
							message: 'Season end month must be after season start month',
						});
					}
				}
			}
		}),
	});

	const saveGeneralSettingsMutation = useSaveCompanyHotelGeneralSettingsMutation({
		onSuccess: () => {
			toaster.success('Settings', 'General settings saved successfully');
		},
	});

	const form = useAppForm({
		defaultValues: {
			currency: companyHotel.settings.main.currency,
			ignorePartnerOffers: companyHotel.settings.main.ignorePartnerOffers,
			nights: companyHotel.settings.defaultParsing.nights,
			operation: companyHotel.settings.operation,
		},
		validators: {
			// @ts-ignore
			onChange: FormValidation,


		},
		onSubmit: ({ value }) => saveGeneralSettingsMutation.mutateAsync({
			companyHotelId: companyHotel.id,
			input: value,
		}).catch((e:string) => toaster.error('Settings', e)),

	});

	return <form.AppForm>
		<form onSubmit={(e) => {
			e.preventDefault();
			e.stopPropagation();
		}}>
			<div className="bg-white p-4 sm:p-10 rounded-xl">
				<div><h2 className="text-base font-semibold leading-5 text-gray-900">General Settings</h2>
					<p className="text-sm leading-6 text-gray-500">Configuration settings regarding your property.</p>
					<fieldset className="mt-3 border-t border-gray-200">
						<div className="mt-6">
							<p className="font-medium text-sm text-gray-900">Operational Period</p>
							<div className="space-y-4 md:flex md:items-center md:space-x-10 md:space-y-0 mt-2">
								<form.AppField name="operation.type">
									{(field) => <>
										<field.RadioField value={OperationType.ALL_YEAR} label="All Year" />
										<RadioField value={OperationType.SEASONAL} label="Seasonal" />
									</>}
								</form.AppField>
								<form.Subscribe selector={(state) => state.values.operation.type}>
									{(type) => {
										if (type === OperationType.SEASONAL) {
											return <>
												<div
													className="sm:flex items-center sm:space-x-5 space-y-2 md:space-y-0 bg-gray-100 px-4 py-2 rounded-xl">
													<div className="flex items-center space-x-2">
														<div className="flex justify-between">
															<label className="label">Start</label>
														</div>
														<form.AppField name="operation.seasonFrom">
															{(field) => {
																return <field.SelectField placeholder="Month" optionsTextSize="sm" hideErrorMessage
																													options={Object.values(Month).map(m => {
																														return { id: m, label: _capitalize(m), value: m };
																													})} />;
															}}
														</form.AppField>

													</div>
													<div className="flex items-center space-x-2">
														<div className="flex justify-between">
															<label htmlFor="input-normal" className="label">End</label>
														</div>
														<form.AppField name="operation.seasonTo">
															{(field) => {
																return <field.SelectField placeholder="Month"
																													optionsTextSize="sm"
																													hideErrorMessage
																													options={Object.values(Month).map(m => {
																														return { id: m, label: _capitalize(m), value: m };
																													})} />;
															}}
														</form.AppField>
													</div>
												</div>
											</>;
										}
										return null;
									}}
								</form.Subscribe>
							</div>
							<form.AppField name="currency">
								{(field) => <div className="mt-6">
									<p className="font-medium text-sm text-gray-900">Currency<span
										className="ml-2 text-xs font-light italic text-gray-400">(Default prices currency)</span></p>
									<div className="mt-1 max-w-md">
										<field.SelectField
											size="w-full"
											optionsSize="xlarge"
											options={[
												{ id: 'EUR', label: 'EUR - Euro (€)', value: 'EUR' },
												{ id: 'USD', label: 'USD - United States Dollar ($)', value: 'USD' },
												{ id: 'GBP', label: 'GBP - British Pound (£)', value: 'GBP' },
											]} />
									</div>
								</div>}
							</form.AppField>
							<form.AppField name="nights">
								{(field) => <div className="mt-6">
									<p className="font-medium text-sm text-gray-900">Nights Stay<span
										className="ml-2 text-xs font-light italic text-gray-400">(Up to 2 selections with Premium plan)</span>
									</p>
									<div className="mt-1 max-w-md">
										<field.MultiSelectField placeholder="Select..."
																						maxSelections={2}
																						size='w-full'
																						optionsSize='large'
																						options={[
																							{ id: '1', label: '1 Night', value: 1 },
																							{ id: '2', label: '2 Nights', value: 2 },
																							{ id: '3', label: '3 Nights', value: 3 },
																							{ id: '4', label: '4 Nights', value: 4 },
																							{ id: '5', label: '5 Nights', value: 5 },
																							{ id: '6', label: '6 Nights', value: 6 },
																							{ id: '7', label: '7 Nights', value: 7 },
																							{ id: '8', label: '8 Nights', value: 8 },
																						]} />
									</div>
								</div>}
							</form.AppField>
							<form.AppField name="ignorePartnerOffers">
								{(field) => <div className="mt-6">
									<Field as="div" className="flex gap-x-4 sm:col-span-2 mt-3">
										<div className="flex h-6 items-center">
											<field.SwitchField label="Ignore partner offers" />
											<Label className="text-sm leading-6 text-slate-600 ml-2">
												Ignore partner offers
											</Label>
										</div>
									</Field>
								</div>}
							</form.AppField>
						</div>
					</fieldset>
					<div className="flex items-center justify-end gap-x-4 border-t border-gray-200 pt-4 mt-6">
						<ResetButton>Reset</ResetButton>
						<SubmitButton state="primary">Save</SubmitButton>
					</div>
				</div>
			</div>
		</form>
	</form.AppForm>;
});

export default RateshopperGeneralSettingsPage;